import { ReadonlyVec3, vec3 } from 'gl-matrix';
import {
    SummaryStatistics,
    summaryStatistics,
} from '~/domain/statistics/summaryStatistics';
import { Grid } from '~/engine/data/common/Grid';
import { SDF_ISO_THRESHOLD } from './sdfConstants';

/**
 * Mean, SD, min and max of the image intensity inside a structure's mask, in
 * the image's native units. For every mask voxel whose distance-field value
 * is above `threshold`, the image is read at that voxel's centre. A read that
 * falls outside the image, or is not a finite number, is left out. Returns
 * null when nothing was left to count.
 *
 * @param pixels Per-Z-slice SDF bytes (`dimX * dimY` each).
 * @param textureGrid The SDF's own grid, mapping voxel centers to patient mm.
 * @param sampleImageAtWorld Image value at a patient point, null off-image.
 *   Injected so the traversal is testable without a volume.
 */
export function maskIntensityStatistics(
    pixels: readonly Uint8Array[],
    dimX: number,
    dimY: number,
    dimZ: number,
    textureGrid: Grid,
    sampleImageAtWorld: (world: ReadonlyVec3) => number | null,
    threshold: number = SDF_ISO_THRESHOLD
): SummaryStatistics | null {
    // The values are fed to the statistics one at a time rather than
    // gathered into an array first, since a mask can hold millions of
    // voxels. They come in a fixed order (z, then y, then x) so the result
    // never varies between runs. A read outside the image becomes NaN, which
    // summaryStatistics leaves out.
    function* maskedImageSamples(): Generator<number> {
        const voxel = vec3.create();
        for (let z = 0; z < dimZ; z++) {
            const slice = pixels[z];
            for (let y = 0; y < dimY; y++) {
                const row = y * dimX;
                for (let x = 0; x < dimX; x++) {
                    if (slice[row + x] > threshold) {
                        vec3.set(voxel, x, y, z);
                        const world =
                            textureGrid.voxelCenterToWorldSpace(voxel);
                        yield sampleImageAtWorld(world) ?? Number.NaN;
                    }
                }
            }
        }
    }
    return summaryStatistics(maskedImageSamples());
}
